import { useToast } from '@bv/composables/common/toast';
import { io } from 'socket.io-client';

import { userDetailsStore } from '@/stores/auth/user-details.store';
import { useDashboardStore } from '@/stores/reports/dashboard.store';
import { livePanelStore } from '@/stores/socket/live-panel-socket.store';
import { useSocketStore } from '@/stores/socket/socket.store';

const toast = useToast();

class SocketHandler {
  constructor() {
    this.socket = null;
    this.socketUrl = import.meta.env.VITE_SOCKET_URL;
  }

  init() {
    this.userStore = userDetailsStore();
    const currentWorkspace = this.userStore.getCurrentWorkspace;
    const token = this.userStore.getToken;

    this.socket = io(this.socketUrl, {
      transports: ['websocket'],
      query: {
        access_token: token,
        workspaceId: currentWorkspace?.id,
      },
    });

    this.livePanelStore = livePanelStore();
    this.dashboardStore = useDashboardStore();

    this.registerEventListeners();
  }

  async registerEventListeners() {
    this.socket.on('connect', this.onOpen.bind(this));
    this.socket.io.on('reconnect_attempt', this.onReconnectAttempt.bind(this));
    this.socket.on('connect_error', this.onError.bind(this));

    await this.livePanelStore.getAvailableAgents();

    const events = [
      'ServerTime',

      'DashboardStatsUpdated',
      'Stats',
      'WaitingCallsUpdated',

      'ExtensionDetails',
      'QueueDetails',
      'TeamDetails',
      'Calls',

      'ExtensionStatusChanged',
      'PresenceStatusChanged',
      // 'DeviceStateChanged', // check is existing

      'QueueMemberStatusChanged',

      'AssignExtension',
      'UnAssignExtension',

      'CallStarted',
      'CallUpdated',
      'CallEnded',

      'PickupResponse',
    ];

    events.forEach((eventName) => {
      this.socket.on(eventName, this.onEvent.bind(this, eventName));
    });
  }

  onEvent(event, data) {
    switch (event) {
      case 'ServerTime':
        this.livePanelStore.updateServerTimeDiff(data);
        break;

      case 'DashboardStatsUpdated':
        this.dashboardStore.updateDashboardStats(data);
        this.livePanelStore.updateDashboardStats(data);
        break;
      case 'Stats':
        this.livePanelStore.updateAvailableAgents(data);
        break;
      case 'WaitingCallsUpdated':
        this.livePanelStore.updateWaitingCalls(data);
        break;

      case 'ExtensionDetails':
        this.livePanelStore.initExtensions(data);
        break;
      case 'QueueDetails':
        this.livePanelStore.initQueues(data);
        break;
      case 'TeamDetails':
        this.livePanelStore.initTeams(data);
        break;
      case 'Calls':
        this.livePanelStore.initCalls(data);
        break;

      case 'ExtensionStatusChanged':
        this.livePanelStore.updateExtension(data);
        break;
      case 'PresenceStatusChanged':
        this.livePanelStore.updateExtensionPresence(data);
        break;

      case 'DeviceStateChanged':
        break;

      case 'QueueMemberStatusChanged':
        this.livePanelStore.updateQueueMember(data);
        break;

      case 'AssignExtension':
        this.assignExtension(data);
        break;
      case 'UnAssignExtension':
        this.unAssignExtension(data);
        break;

      case 'CallStarted':
        this.livePanelStore.addCall(data);
        break;
      case 'CallUpdated':
        this.livePanelStore.updateCall(data);
        break;
      case 'CallEnded':
        this.livePanelStore.removeCall(data);
        break;

      case 'PickupResponse':
        this.pickupResponse(data);
        break;
    }
  }

  assignExtension(data) {
    if (this.userStore.userData.id === data.user_id && this.userStore.currentWorkspace.id === data.workspaceId) {
      this.userStore.assignExtension();
    }
  }

  unAssignExtension(data) {
    if (this.userStore.userData.id === data.user_id && this.userStore.currentWorkspace.id === data.workspaceId) {
      this.userStore.unAssignExtension();
    }
  }

  pickupResponse(data) {
    if (data?.error?.code === 404) {
      toast.error(data?.error?.message);
    }
  }

  onOpen() {
    const socketStore = useSocketStore();
    socketStore.reset();
    socketStore.setStatus('connected');
    this.socket.emit('getServerTime', {});
    this.socket.emit('getQueues', {});
    this.socket.emit('getExtensions', {});
    this.socket.emit('getTeams', {});
    this.socket.emit('getCalls', {});
  }

  onReconnectAttempt() {
    const socketStore = useSocketStore();

    if (socketStore.getConnectingTimes < 10) {
      socketStore.setConnectingTimes();
      socketStore.setStatus('reconnecting');
    } else {
      this.socket.disconnect();
      socketStore.setStatus('failed');
    }
  }

  disconnectConnection() {
    this.socket.disconnect();
  }

  onError(error) {
    if (error.message === 'Authentication error') {
      useSocketStore().setStatus('failed');
    }
  }

  onDisconnect() {
    useSocketStore().setStatus('failed');
  }

  sendAction(name, data) {
    this.socket.emit(name, data);
  }
}

const socketHandler = new SocketHandler();

export default socketHandler;
